import { useEffect, useMemo, useState } from "react";
import ReactECharts from "echarts-for-react";
import { useParams } from "react-router-dom";
import { get } from "lodash";

import { getRegistryConfig, getRegistryInfo } from "service/gatewayService";
import { getGatewayType } from "utils/gatewayFunctions";
import { GatewayType } from "constant/gateway";
import { isHttpSuccess } from "utils/functions";

type HierarchyDetailsType = {
    name: string;
    id?: string;
    children: Array<any>;
};

const HierarchyTab = (props: any) => {
    const params: any = useParams();
    const [currentLoading, setCurrentLoading] = useState<number>(Date.now());
    const [gatewayData, setGatewayData] = useState<{
        status: number;
        data: any;
    }>();
    const [registryConfig, setRegistryConfig] = useState<{
        status: number;
        data: any;
    }>();
    const [width, setWidth] = useState(window.innerWidth);

    const isPcsGateway = getGatewayType(gatewayData?.data) === GatewayType.PCS;
    const { ldsuStatuses } = props;

    const getItemStyle = (device_id: string) => {
        device_id = device_id.split("-")[0];
        const status = ldsuStatuses[device_id] || false;

        return {
            id: device_id,
            symbol: "circle",
            itemStyle: {
                color: status ? "green" : "red",
                borderWidth: 1.5,
                borderColor: "#b0c4de",
            },
        };
    };

    const hierarchyDetails = useMemo<HierarchyDetailsType>(() => {
        const renderLDSChildren = (
            infoList: any,
            configList: any,
            BUSNumber: string
        ) => {
            const combined: any = {};

            const filteredInfoList = infoList.filter(
                ({ PORT }: { PORT: string }) => PORT === BUSNumber
            );

            filteredInfoList.forEach((i: any) => {
                if (!combined[i.UID]) {
                    combined[i.UID] = {};
                }

                const config = configList.find(
                    ({ UID }: { UID: string }) => UID === i.UID
                );

                Object.assign(combined[i.UID], {
                    uuid: i.uuid,
                    name: config?.name ?? i.NAME,
                    children: get(config, "sensor_names", []).map(
                        (k: any, index: number) => {
                            let sensorObj = {
                                name: k,
                                ...getItemStyle(i.UID + "-" + index),
                            };
                            return sensorObj;
                        }
                    ),
                    ...getItemStyle(i.UID),
                });
            });

            return Object.values(combined);
        };

        if (
            !gatewayData?.status ||
            !registryConfig?.status ||
            !isHttpSuccess(gatewayData.status) ||
            !isHttpSuccess(registryConfig.status)
        ) {
            return {
                name: "",
                children: [],
            };
        }

        const gatewayType = getGatewayType(gatewayData.data);
        const isPcsGateway =
            getGatewayType(gatewayData?.data) === GatewayType.PCS;

        let children = [];

        if (isPcsGateway) {
            children = gatewayData.data.devices
                .map((device: any) => {
                    const config = registryConfig.data.find(
                        ({ UID }: { UID: string }) => UID === device.UID
                    );

                    const children = get(config, "sensor_names", []).map(
                        (name: string) => ({
                            name,
                            ...getItemStyle(device.UID),
                        })
                    );

                    return children;
                })
                .flat();
        } else if (gatewayType === GatewayType.LoRaWAN) {
            children = [
                {
                    name: "Modbus",
                    ...getItemStyle(params.gatewayId),
                    children: renderLDSChildren(
                        gatewayData.data.devices,
                        registryConfig.data,
                        "1"
                    ),
                },
            ];
        } else {
            children = [
                {
                    name: "Machine-to-Machine (M2M)",
                },
                {
                    name: "LDSBUS 1",
                    children: renderLDSChildren(
                        gatewayData.data.devices,
                        registryConfig.data,
                        "1"
                    ),
                },
                {
                    name: "LDSBUS 2",
                    children: renderLDSChildren(
                        gatewayData.data.devices,
                        registryConfig.data,
                        "2"
                    ),
                },
                {
                    name: "LDSBUS 3",
                    children: renderLDSChildren(
                        gatewayData.data.devices,
                        registryConfig.data,
                        "3"
                    ),
                },
            ];
        }

        const table = {
            name: props.name,
            ...getItemStyle(params.gatewayId),
            children,
            // Root node gets left positioning
            label: {
                position: "left",
                verticalAlign: "middle",
                align: "right",
            },
        };

        return table;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        gatewayData,
        ldsuStatuses,
        registryConfig,
        props.name,
        params.gatewayId,
    ]);

    useEffect(() => {
        let isMounted = true;

        const fetch = async () => {
            const [registryConfig, registryInfo]: any[] = await Promise.all([
                getRegistryConfig(params.gatewayId),
                getRegistryInfo(params.gatewayId),
            ]);

            // Only update state if component is still mounted
            if (isMounted) {
                setRegistryConfig(registryConfig);
                setGatewayData(registryInfo);
            }
        };
        fetch();

        // Cleanup function to prevent state updates after unmount
        return () => {
            isMounted = false;
        };
    }, [params.gatewayId, props.name]);

    useEffect(() => {
        const handleResize = () => setWidth(window.innerWidth);

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        setCurrentLoading(Date.now());
    }, [ldsuStatuses]);

    const option = {
        tooltip: {
            trigger: "item",
            triggerOn: "mousemove",
        },
        series: [
            {
                type: "tree",
                data: [hierarchyDetails],

                top: "0%",
                left: isPcsGateway ? "35%" : "15%",
                bottom: "0%",
                right: isPcsGateway ? "35%" : "15%",

                symbolSize: 10,

                label: {
                    position: "top",
                    verticalAlign: "middle",
                    align: "center",
                    fontSize: 16,
                    color: "#ffffff",
                    fontFamily: "Roboto",
                    fontWeight: 400,
                    overflow: "truncate",
                    width: 250,
                },

                leaves: {
                    label: {
                        position: "right",
                        verticalAlign: "middle",
                        align: "left",
                    },
                },

                emphasis: {
                    focus: "ancestor",
                },

                expandAndCollapse: true,
                animationDuration: 550,
                animationDurationUpdate: 750,
            },
        ],
    };

    // Update label width based on window width
    if (width <= 1024) {
        option.series[0].label.width = 100;
    } else if (width <= 1440) {
        option.series[0].label.width = 175;
    }
    return (
        <div className="height-90vh">
            <div>
                <ReactECharts
                    className="height-90vh"
                    option={option}
                    loadingOption={{
                        ts: currentLoading,
                    }}
                />
            </div>
        </div>
    );
};

export default HierarchyTab;
